import * as React from "react";
import {
  ContractSectionProps,
  LaborSectionProps,
  MaterialSectionProps,
  LotExtraSectionProps,
  OptionsSectionProps,
  SoftCostSectionProps,
  RunningTotalsProps,
} from "../../../types";

interface UseRunningTotalsArgs {
  contractSection: ContractSectionProps;
  laborSection: LaborSectionProps;
  materialSection: MaterialSectionProps;
  lotExtraSection: LotExtraSectionProps;
  optionsSection: OptionsSectionProps;
  softCostSection: SoftCostSectionProps;
  exitStrategy: number | undefined;
  paType?: "spec" | "sold";
  bpPercent?: number | null;
  blPercent?: number | null;
}

interface SpecScenario {
  price: number;
  profit: number;
  percentMargin: number | null | undefined;
  realtorCommissions: number;
  inHouseCommissions: number;
  builderBonus: number;
  totalCost: number;
  discountToFullRetail: number;
}

export function useRunningTotals({
  contractSection,
  laborSection,
  materialSection,
  lotExtraSection,
  optionsSection,
  softCostSection,
  exitStrategy,
  paType,
  bpPercent,
  blPercent,
}: UseRunningTotalsArgs): RunningTotalsProps {
  // ── Contract & retail ────────────────────────────────────────────────────────

  const fullRetail = React.useMemo(() => {
    const {
      BrochurePrice = 0,
      LotPremium = 0,
      OptionalArchitecturalPlanChanges = 0,
      CustomArchitecturalPlanChanges = 0,
      BuyerSelectedOptions = 0,
      OptionsGiven = 0,
      OptionsRetailValue = 0,
      CashOptionsPaid = 0,
    } = contractSection ?? {};

    const baseRetail =
      BrochurePrice +
      LotPremium +
      OptionalArchitecturalPlanChanges +
      BuyerSelectedOptions;

    const nonSpecRetail =
      CustomArchitecturalPlanChanges +
      OptionsGiven +
      OptionsRetailValue +
      CashOptionsPaid;

    return baseRetail + (paType === "spec" ? 0 : nonSpecRetail);
  }, [contractSection, paType]);

  const contract = React.useMemo(
    () =>
      fullRetail -
      (contractSection?.SalesAllowanceOptions || 0) -
      (contractSection?.SalesAllowanceClosingCosts || 0) -
      (contractSection?.SalesAllowancePriceReduced || 0),
    [fullRetail, contractSection]
  );

  // ── Labor / material / lot / options ────────────────────────────────────────

  const labor =
    (laborSection?.LaborBudget || 0) +
    (laborSection?.ExtraLaborActual || 0) +
    (laborSection?.SpecialLabor || 0);

  const material =
    (materialSection?.MaterialBudget || 0) +
    (materialSection?.ExtraMaterialBudget || 0) +
    (materialSection?.SpecialMaterial || 0);

  const lotExtras = lotExtraSection?.LotExtras || 0;

  const options = optionsSection?.CostOfOptionsWithContract || 0;

  const laborMaterial = React.useMemo(
    () => labor + material + lotExtras + options,
    [labor, material, lotExtras, options]
  );

  // ── Soft costs ───────────────────────────────────────────────────────────────

  const softCost = React.useMemo(
    () =>
      (softCostSection?.LotCost || 0) +
      (softCostSection?.Discretionary || 0) +
      (softCostSection?.RealtorCommissions || 0) +
      (softCostSection?.SpecialMarketingIncentive || 0) -
      (softCostSection?.RealtorCommissionsadjusted || 0) +
      (softCostSection?.InHouseCommissions || 0) +
      (softCostSection?.InterimInterestBudget || 0) +
      (softCostSection?.ClosingCostBudget || 0) +
      (softCostSection?.BuilderBonus || 0),
    [softCostSection]
  );

  const totalCost = React.useMemo(
    () => laborMaterial + softCost,
    [laborMaterial, softCost]
  );

  // ── Sold profit ──────────────────────────────────────────────────────────────

  const profitUSD = paType === "sold" ? contract - totalCost : 0;

  const profitMargin =
    paType === "sold" && contract ? (profitUSD / contract) * 100 : 0;

  const varianceToExitStrategy =
    paType === "sold" ? profitMargin - (exitStrategy ?? 0) : 0;

  // ── Spec: closed-form sale-price solver ──────────────────────────────────────
  //
  // Realtor (3%), InHouseCommissions (2%), BuilderBonus (0.5%) are computed as
  // percentages of the back-calculated sale price P.
  //
  // Let C = specBaseCost (all costs except those three rate-driven fields)
  // combinedRatePct = (hasRealtor ? 3 : 0) + 2 + 0.5   (percentage points)
  //
  // Solve: P = C * 100 / (100 - targetPct - combinedRatePct)

  const specBaseCost = React.useMemo(() => {
    if (paType !== "spec") return 0;
    return (
      laborMaterial +
      (softCostSection?.LotCost || 0) +
      (softCostSection?.Discretionary || 0) +
      (softCostSection?.SpecialMarketingIncentive || 0) -
      (softCostSection?.RealtorCommissionsadjusted || 0) +
      (softCostSection?.InterimInterestBudget || 0) +
      (softCostSection?.ClosingCostBudget || 0)
    );
  }, [paType, laborMaterial, softCostSection]);

  const hasRealtor = softCostSection?.HasRealtor ?? false;
  const combinedRatePct = (hasRealtor ? 3 : 0) + 2 + 0.5;

  const solveSpecSalePrice = React.useCallback(
    (targetPct: number | null | undefined): number => {
      if (paType !== "spec" || targetPct == null) return 0;
      const denominator = 100 - targetPct - combinedRatePct;
      if (denominator <= 0) return 0;
      return (100 * specBaseCost) / denominator;
    },
    [paType, specBaseCost, combinedRatePct]
  );

  // ── Shared spec-scenario builder ─────────────────────────────────────────────

  const buildSpecScenario = React.useCallback(
    (targetPct: number | null | undefined): SpecScenario => {
      const price = Math.round(solveSpecSalePrice(targetPct) * 100) / 100;
      const realtorCommissions =
        paType === "spec" ? Math.round(price * (hasRealtor ? 0.03 : 0) * 100) / 100 : 0;
      const inHouseCommissions =
        paType === "spec" ? Math.round(price * 0.02 * 100) / 100 : 0;
      const builderBonus =
        paType === "spec" ? Math.round(price * 0.005 * 100) / 100 : 0;
      const scenarioTotalCost =
        paType === "spec"
          ? Math.round((specBaseCost + realtorCommissions + inHouseCommissions + builderBonus) * 100) / 100
          : totalCost;
      return {
        price,
        profit: paType === "spec" ? Math.round((price - scenarioTotalCost) * 100) / 100 : 0,
        percentMargin: paType === "spec" && price ? targetPct : 0,
        realtorCommissions,
        inHouseCommissions,
        builderBonus,
        totalCost: scenarioTotalCost,
        discountToFullRetail: paType === "spec" ? Math.round((fullRetail - price) * 100) / 100 : 0,
      };
    },
    [paType, solveSpecSalePrice, hasRealtor, specBaseCost, totalCost, fullRetail]
  );

  // ── BP / BL / Exit scenarios ─────────────────────────────────────────────────

  const bp = React.useMemo(
    () => buildSpecScenario(bpPercent),
    [buildSpecScenario, bpPercent]
  );

  const bl = React.useMemo(
    () => buildSpecScenario(blPercent),
    [buildSpecScenario, blPercent]
  );

  const exit = React.useMemo(
    () => buildSpecScenario(exitStrategy ?? null),
    [buildSpecScenario, exitStrategy]
  );

  // ── Soft cost subtotal split by BL/BP ────────────────────────────────────────
  // Same shape as `softCost` but swaps in the rate-driven commission fields
  // per scenario. Falls back to the flat `softCost` for non-spec paTypes.

  const softCostBase = React.useMemo(
    () =>
      (softCostSection?.LotCost || 0) +
      (softCostSection?.Discretionary || 0) +
      (softCostSection?.SpecialMarketingIncentive || 0) -
      (softCostSection?.RealtorCommissionsadjusted || 0) +
      (softCostSection?.InterimInterestBudget || 0) +
      (softCostSection?.ClosingCostBudget || 0),
    [softCostSection]
  );

  const softCostBL = React.useMemo(
    () =>
      paType === "spec"
        ? Math.round((softCostBase + bl.realtorCommissions + bl.inHouseCommissions + bl.builderBonus) * 100) / 100
        : softCost,
    [paType, softCostBase, bl, softCost]
  );

  const softCostBP = React.useMemo(
    () =>
      paType === "spec"
        ? Math.round((softCostBase + bp.realtorCommissions + bp.inHouseCommissions + bp.builderBonus) * 100) / 100
        : softCost,
    [paType, softCostBase, bp, softCost]
  );


  console.log("Running totals", {
    bpProfit: bp.profit,
    blProfit: bl.profit,
    bpPrice: bp.price,
    blPrice: bl.price,
    exitPrice: exit.price,
  });

  return {
    fullRetail,
    contract,
    labor,
    material,
    lotExtras,
    options,
    laborMaterial,
    softCost,
    totalPrice: contract,
    profitUSD,
    profitMargin,
    varianceToExitStrategy,
    softCostBL,
    softCostBP,
    // BP scenario
    discountToBP: bp.discountToFullRetail,
    bpPrice: bp.price,
    bpProfit: bp.profit,
    bpPercent: bp.percentMargin,
    bpRealtorCommissions: bp.realtorCommissions,
    bpInHouseCommissions: bp.inHouseCommissions,
    bpBuilderBonus: bp.builderBonus,
    totalCostBP: bp.totalCost,
    // BL scenario
    discountToBL: bl.discountToFullRetail,
    blPrice: bl.price,
    blProfit: bl.profit,
    blPercent: bl.percentMargin,
    blRealtorCommissions: bl.realtorCommissions,
    blInHouseCommissions: bl.inHouseCommissions,
    blBuilderBonus: bl.builderBonus,
    totalCostBL: bl.totalCost,
    // Exit scenario
    discountToExit: exit.discountToFullRetail,
    exitPrice: exit.price,
    exitProfit: exit.profit,
    exitPercent: exit.percentMargin,
    exitRealtorCommissions: exit.realtorCommissions,
    exitInHouseCommissions: exit.inHouseCommissions,
    exitBuilderBonus: exit.builderBonus,
    totalCostExit: exit.totalCost,
  };
}
